import React from 'react';

import Box from 'components/Box';
import BoxBody from 'components/BoxBody';
import Button from 'components/ButtonDeprecated';
import Row from 'components/Row';
import Text from 'components/Text';
import TextWithIcon from 'components/TextWithIcon';
import {
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_participantMetadata,
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_coachingSessionSchedules,
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_upcomingCoachingSessionInstances,
  GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_sessionTools,
} from 'coaching/queries/__generated__/GetCoachingHomeDashboard';
import MediaObject from 'components/MediaObject';
import CoachingConstants from 'coaching/CoachingConstants';
import { getFilteredListOfUpcomingInstances } from 'coaching/lib/coachingSessionInstances';
import { coachingHomeEventPrefix } from 'coaching/components/DeprecatedCoachingHomeView/CoachingHomeView';
import CoachingNoUpcomingSessionsCard from 'coaching/components/DeprecatedCoachingHomeView/CoachingNoUpcomingSessionsCard';
import CoachingHomeFreeTrial from 'coaching/components/DeprecatedCoachingHomeView/CoachingHomeFreeTrial';
import CoachingSessionUpcomingInstanceRows from 'coaching/components/DeprecatedCoachingHomeView/CoachingSessionUpcomingInstanceRows';
// @ts-ignore
import userAgent from 'lib/currentUserAgent';

import 'coaching/components/styles/CoachingSessionsBySubjectContainer.scss';

export type ParticipantMetadata = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_participantMetadata;
export type CoachingSessionSchedule = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_coachingSessionSchedules;
export type CoachingSessionInstance = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_upcomingCoachingSessionInstances;
export type CoachingSessionTools = GetCoachingHomeDashboard_coaching_getCoachingHomeDashboard_participants_sessionTools;

export type Props = {
  subjectName?: string;
  recurringSessionSchedules?: CoachingSessionSchedule[];
  participantMetadata: ParticipantMetadata;
  upcomingInstances?: CoachingSessionInstance[];
  sessionTools?: CoachingSessionTools;
  hasOptOutExperiment: boolean;
  hasStudentInvitation: boolean;
  userUuid: string;
};

export default function CoachingSessionsBySubjectContainer(props: Props) {
  if (!props.subjectName) {
    // if we don't have a subjectName, just show an empty box
    return (
      <CoachingSessionsBySubjectBox>
        <CoachingNoUpcomingSessionsCard />
      </CoachingSessionsBySubjectBox>
    );
  }

  const upcomingFreeTrialSession = props.upcomingInstances?.find(i => i.isFreeTrial);
  const filteredAndOrderedInstances = getFilteredListOfUpcomingInstances(props.upcomingInstances);
  const sharedProps = {
    subjectName: props.subjectName,
    recurringSessionSchedules: props.recurringSessionSchedules,
    upcomingInstances: filteredAndOrderedInstances,
  };

  return (
    <CoachingSessionsBySubjectBox {...sharedProps}>
      {upcomingFreeTrialSession && props.sessionTools ? (
        <CoachingHomeFreeTrial
          instance={upcomingFreeTrialSession}
          participantMetadata={props.participantMetadata}
          userUuid={props.userUuid}
          hasStudentInvitation={props.hasStudentInvitation}
          sessionTools={props.sessionTools}
        />
      ) : filteredAndOrderedInstances && filteredAndOrderedInstances.length > 0 ? (
        <CoachingSessionUpcomingInstanceRows
          instances={filteredAndOrderedInstances}
          sessionTools={props.sessionTools}
          recurringScheduleUuids={props.recurringSessionSchedules?.map(s => s.uuid) ?? []}
        />
      ) : (
        <CoachingNoUpcomingSessionsCard />
      )}
    </CoachingSessionsBySubjectBox>
  );
}

function CoachingSessionsBySubjectBox({
  subjectName,
  recurringSessionSchedules,
  upcomingInstances,
  children,
}: {
  subjectName?: string;
  recurringSessionSchedules?: CoachingSessionSchedule[];
  upcomingInstances?: CoachingSessionInstance[];
  children: React.ReactNode;
}) {
  return (
    <Row>
      <Box
        noShadow
        textAlign="left"
        bandHeader={
          subjectName
            ? constructSubjectHeader(subjectName, recurringSessionSchedules, upcomingInstances)
            : ''
        }>
        {children}
      </Box>
    </Row>
  );
}

function hasFreeTrial(instances?: CoachingSessionInstance[]) {
  return instances?.some(i => i.isFreeTrial);
}

function calculateRescheduleButtonText(instances?: CoachingSessionInstance[]) {
  if (!instances || instances.length === 0) {
    // do not show "Reschedule" if user has no upcoming instances
    // because we will show a separate button for requesting sessions
    return undefined;
  }
  return 'Request reschedule';
}

function calculateCadenceInformation(
  recurringSchedules?: CoachingSessionSchedule[],
  instances?: CoachingSessionInstance[]
) {
  function CadenceText({ children }: { children: React.ReactNode }) {
    return (
      <Text size="xsmall" color="graphite">
        {children}
      </Text>
    );
  }

  if (hasFreeTrial(instances)) {
    return <CadenceText>Free trial session</CadenceText>;
  } else if (recurringSchedules?.length === 1) {
    return <CadenceText>{recurringSchedules[0].description}</CadenceText>;
  } else {
    return null;
  }
}

function constructSubjectHeader(
  subjectName: string,
  recurringSchedules?: CoachingSessionSchedule[],
  instances?: CoachingSessionInstance[]
) {
  const rescheduleButtonText = calculateRescheduleButtonText(instances);
  const cadenceInformation = calculateCadenceInformation(recurringSchedules);
  const coachNameString = Boolean(recurringSchedules?.length)
    ? `with ${recurringSchedules?.map(s => `Coach ${s.coach.name.split(' ')[0]}`).join(', ')}`
    : undefined;
  const textComponent = (
    <Text weight="semibold" size="medium">
      {subjectName} {coachNameString}
    </Text>
  );
  const textRowComponent = userAgent.isDesktop() ? (
    <MediaObject center gutter="small">
      {textComponent}
      {cadenceInformation}
    </MediaObject>
  ) : (
    <>
      <Row size="small">{textComponent}</Row>
      {cadenceInformation}
    </>
  );

  return (
    <MediaObject figure="right" gutter="small" center>
      {textRowComponent}
      {rescheduleButtonText && (
        <Button
          level="icon"
          eventKey={`${coachingHomeEventPrefix}.request_reschedule.click`}
          to={`${CoachingConstants.COACHING_HELP_FORM_ROUTE}?request=c_reschedule_session`}
          target="_blank">
          <TextWithIcon
            className="CoachingSessionsBySubjectContainer-rescheduleButton"
            icon="calendar"
            size="small"
            weight="semibold">
            {rescheduleButtonText}
          </TextWithIcon>
        </Button>
      )}
    </MediaObject>
  );
}
